import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { bandOf, BAND_TONE, raidKey } from '@/lib/raid';
import { useTracker } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { Badge } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { AiButton } from './Assistant';

interface Candidate {
  title: string;
  description: string;
  likelihood: number;
  impact: number;
  mitigation: string;
}

/** Candidate risks for a phase from the assistant: accept each into the log, or discard it. */
export function SuggestRisks({ program }: { program: Program }) {
  const tracker = useTracker(program.id);
  const qc = useQueryClient();
  const toast = useToast();
  const [picking, setPicking] = useState(false);
  const [phaseId, setPhaseId] = useState('');
  const [result, setResult] = useState<{ runId: string; risks: Candidate[] } | null>(null);
  const suggest = useMutation({
    mutationFn: () =>
      api.post<{ runId: string; risks: Candidate[] }>(`/api/programs/${program.id}/ai/suggest-risks`, { phaseId }),
    onSuccess: (r) => {
      setPicking(false);
      setResult(r);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const accept = useMutation({
    mutationFn: async (c: Candidate) => {
      const item = await api.post<{ id: string; ref: string }>(`/api/programs/${program.id}/raid`, {
        type: 'RISK',
        title: c.title,
        description: c.description,
        likelihood: c.likelihood,
        impact: c.impact,
        mitigation: c.mitigation,
        phaseId,
      });
      await api.post(`/api/programs/${program.id}/ai/runs/${result!.runId}/accept`, {
        recordType: 'RaidItem',
        recordId: item.id,
      });
      return { item, c };
    },
    onSuccess: ({ item, c }) => {
      toast.ok(`${item.ref} added to the RAID log.`);
      setResult((r) => (r ? { ...r, risks: r.risks.filter((x) => x !== c) } : r));
      void qc.invalidateQueries({ queryKey: raidKey(program.id) });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <>
      <AiButton program={program} size="md" onClick={() => setPicking(true)}>
        Suggest risks
      </AiButton>
      <Dialog
        open={picking}
        onClose={() => setPicking(false)}
        title="Suggest risks for a phase"
        description="The assistant reads the phase plan and the open RAID items, and proposes risks for you to accept or discard."
        footer={
          <>
            <Button variant="secondary" onClick={() => setPicking(false)}>
              Cancel
            </Button>
            <Button onClick={() => suggest.mutate()} loading={suggest.isPending} disabled={!phaseId}>
              Suggest
            </Button>
          </>
        }
      >
        <Field label="Phase" htmlFor="sr-phase">
          <Select id="sr-phase" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
            <option value="">Choose a phase</option>
            {tracker.data?.phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.number}. {p.name}
              </option>
            ))}
          </Select>
        </Field>
      </Dialog>
      <Dialog
        open={!!result}
        onClose={() => setResult(null)}
        wide
        title="Suggested risks"
        description="Proposals only. Accepting one adds it to the RAID log with an AI-assisted badge."
      >
        {result && !result.risks.length && <p className="text-sm text-muted">Nothing left to review.</p>}
        <ul className="space-y-3">
          {result?.risks.map((c, i) => {
            const score = c.likelihood * c.impact;
            return (
              <li key={i} className="rounded-md border border-border p-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className="flex-1 font-medium">{c.title}</span>
                  <Badge tone={BAND_TONE[bandOf(score)]}>
                    {c.likelihood} x {c.impact} = {score}
                  </Badge>
                </div>
                <p className="mt-1 text-muted">{c.description}</p>
                <p className="mt-1">
                  <span className="text-xs text-muted">Mitigation: </span>
                  {c.mitigation}
                </p>
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => accept.mutate(c)}
                    loading={accept.isPending && accept.variables === c}
                  >
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setResult((r) => (r ? { ...r, risks: r.risks.filter((x) => x !== c) } : r))}
                  >
                    Discard
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </Dialog>
    </>
  );
}
